import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// A date-only value ('YYYY-MM-DD', e.g. a SQL DATE column like
// documents.expiration_date) carries no time and no timezone. `new Date('2026-06-03')`
// parses it as UTC midnight, which renders as the PREVIOUS calendar day for any
// viewer west of UTC (Chile, US Central) — the classic "expires a day early" bug.
// Parse the components as LOCAL midnight so the calendar day is preserved regardless
// of the viewer's timezone. Full timestamps (with a 'T' / 'Z' / offset) and Date
// objects are real instants and pass through untouched.
export function parseDateValue(date: string | Date): Date {
  if (date instanceof Date) return date
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (dateOnly) {
    const [, y, m, d] = dateOnly
    return new Date(Number(y), Number(m) - 1, Number(d))
  }
  return new Date(date)
}

// Canonical date display across iCrop: "Jun 3, 2026" (US order, abbreviated month).
// Spelling the month removes the MM/DD vs DD/MM ambiguity for our multi-country users,
// and parseDateValue keeps date-only fields on the correct calendar day in every tz.
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(parseDateValue(date))
}

export function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parseDateValue(date))
}

// For timestamptz values that semantically represent a DEADLINE DATE stored at UTC
// midnight (e.g. accounts.trial_ends_at = '2026-06-30T00:00:00Z'). Pin the display to
// UTC so it shows the intended calendar day instead of slipping a day for viewers west
// of UTC. Use formatDate (local) for real event instants like created_at.
export function formatDateUTC(date: string | Date): string {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(date instanceof Date ? date : new Date(date))
}

// Returns a 'YYYY-MM-DD' string for today, optionally offset by whole days, built from
// LOCAL date components. Use it for comparing against SQL DATE columns (expiration_date)
// so the boundary matches the user's calendar day. toISOString() would use UTC and shift
// the boundary a day for viewers west of UTC, miscounting "expiring in N days".
export function localDateString(offsetDays = 0): string {
  const d = new Date()
  if (offsetDays) d.setDate(d.getDate() + offsetDays)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Split an array into consecutive slices of at most `size` elements. Used to keep
// large `.in(column, ids)` filters within PostgREST's querystring URL limit —
// callers run one query per chunk and merge (e.g. sum head-counts, which are exact
// over disjoint id sets). An empty input yields no chunks.
export function chunk<T>(items: readonly T[], size: number): T[][] {
  if (size < 1) throw new Error(`chunk size must be >= 1, got ${size}`)
  const out: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size))
  }
  return out
}

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export function generateUploadToken(): string {
  return crypto.randomUUID()
}